import React from "react";
import { useQuery } from "react-query";
import "./styles.scss";
import { Button } from "@/shared/components";
import { GetRequest } from "@/api/RequestTemplates";
import { getUserId, isAuthenticated, setAfterLoginRedirectURL } from "@/api/localStorage";
import { useLocation, useNavigate } from "react-router-dom";
import { LoginRoute } from "@/routes/auth";
import ExpandableCard from "@/shared/components/ExpandableCard/ExpandableCard";

// Update the type to match API response
type InvitationStatus = {
  userId: string;
  registeredInvitees: number;
  changeServicesCreated: number;
  changesDone: number;
  positiveTransactions: number;
  negativeTransactions: number;
};

const fetchInvitationStatuses = async (userId: string): Promise<InvitationStatus> => {
  return GetRequest(`${process.env.API_SERVER_URL}/referrals/progress/users/${userId}`).then((res) => res.data);
};

const InvitationStatuses: React.FC = () => {
  const userId = getUserId();
  const isUserAuthenticated = isAuthenticated();
  const navigate = useNavigate();

  const location = useLocation();

  const {
    data = {
      changeServicesCreated: 0,
      changesDone: 0,
      negativeTransactions: 0,
      positiveTransactions: 0,
      registeredInvitees: 0,
    },
    isLoading,
  } = useQuery<InvitationStatus>("invitationStatuses", () => fetchInvitationStatuses(userId), {
    retry: false,
    enabled: isUserAuthenticated, // Only run query if user is authenticated
  });

  if (!isUserAuthenticated) {
    return (
      <div className="not-authenticated-card">
        <div className="card-header">
          <h2 className="login-heading">Verfolgen Sie den Status Ihrer Einladungen</h2>
          <p className="card-subtitle">Bitte melden Sie sich an</p>
        </div>
        <div className="card-body">
          <Button
            onClick={() => {
              setAfterLoginRedirectURL(location.pathname + location.search);
              setTimeout(() => {
                navigate(LoginRoute);
              }, 100);
            }}
          >
            Zum Login
          </Button>
        </div>
      </div>
    );
  }

  if (isLoading) return <div>Loading...</div>;

  return (
    <div className="invitation-statuses">
      <div className="title">Verfolgen Sie den Status Ihrer Einladungen:</div>

      <div className="card-container">
        <ExpandableCard
          title="Angenommene Einladungen:"
          description="Eingeladener Freund hat sich bei remind.me registriert und ist erfolgreich in unserem System erfasst."
          value={data?.registeredInvitees}
          isDisabled={data?.registeredInvitees === 0}
        />

        <ExpandableCard
          title="Wechselservice erstellt:"
          description="Eingeladener Freund hat einen kostenlosen Wechselservice erstellt und ist erfolgreich in unserem System erfasst."
          value={data?.changeServicesCreated}
          isDisabled={data?.changeServicesCreated === 0}
        />

        <ExpandableCard
          title="Wechsel in Bearbeitung:"
          description="Der kostenlose Wechsel des eingeladenen Freundes befindet sich bei unseren Wechselexperten in Bearbeitung."
          value={data?.changesDone}
          isDisabled={data?.changesDone === 0}
        />

        <ExpandableCard
          title="Wechsel bestätigt:"
          description="Wechsel des eingeladenen Freundes ist erfolgreich abgeschlossen. Der Gutschein kann in Ihrem remind.me-Kundenkonto angefordert werden.
Sobald Ihr Gutschein-Code in Ihrem Kundenkonto abrufbar ist, informieren wir Sie per E-Mail. Die Auszahlung erfolgt in der Regel zum Monatsende."
          value={data?.positiveTransactions}
          isDisabled={data?.positiveTransactions === 0}
        />

        <ExpandableCard
          title="Wechsel abgelehnt:"
          description="Wechsel des eingeladenen Freundes wurde abgelehnt. Der Anspruch auf die Gutschein-Prämie entfällt."
          value={data?.negativeTransactions}
          isDisabled={data?.negativeTransactions === 0}
        />
      </div>
    </div>
  );
};

export default InvitationStatuses;
